import { resolveTargetedIntent, INTENT_TYPES, isUnlockIntent } from './targetedIntentResolver.js'

function clone(value) {
  if (Array.isArray(value)) return value.map(clone)
  if (!value || typeof value !== 'object') return value
  return Object.keys(value).reduce((result, key) => {
    result[key] = clone(value[key])
    return result
  }, {})
}

function structuredStatus(data = {}) {
  const snapshot = data.snapshot || {}
  const fields = ['status', 'warning', 'alarm', 'risk', 'abnormal']
  return fields.reduce((result, field) => {
    const value = data[field] !== undefined ? data[field] : snapshot[field]
    if (value !== undefined && value !== null && value !== '') result[field] = clone(value)
    return result
  }, {})
}

export function createReservoirTargetContext(target, page = {}) {
  if (!target) return null
  return {
    type: 'reservoir',
    id: String(target.businessId || target.id || target.objectId || target.key || ''),
    entityId: target.entityId ? String(target.entityId) : undefined,
    name: target.displayName || target.name || '',
    data: clone(target.data || target.businessData || {}),
    page: {
      type: 'cesium',
      scene: 'guxian-water-network',
      ...page,
    },
  }
}

export class MultimodalContextStore {
  constructor(initial = {}) {
    this.state = {
      target: null,
      hoverTarget: null,
      page: { type: 'cesium', scene: 'guxian-water-network' },
      interaction: null,
      voice: {
        status: 'idle',
        utterance: '',
        targetedQuery: null,
        targetedIntent: null,
        createdTask: null,
        understanding: '',
      },
      ...clone(initial),
    }
    this.listeners = new Set()
  }

  subscribe(listener) {
    if (typeof listener !== 'function') return () => {}
    this.listeners.add(listener)
    listener(this.snapshot())
    return () => this.listeners.delete(listener)
  }

  snapshot() { return clone(this.state) }

  setHoverTarget(target) {
    const next = target ? createReservoirTargetContext(target, this.state.page) : null
    const currentKey = this.state.hoverTarget && `${this.state.hoverTarget.id}:${this.state.hoverTarget.entityId || ''}`
    const nextKey = next && `${next.id}:${next.entityId || ''}`
    if (currentKey === nextKey) return this.snapshot()
    this.state.hoverTarget = next
    this._emit()
    return this.snapshot()
  }

  lockTarget(target, lockMethod = 'fist') {
    this.state.target = createReservoirTargetContext(target, this.state.page)
    this.state.interaction = {
      source: ['gesture', 'voice'],
      lockMethod,
      lockedAt: new Date().toISOString(),
    }
    this.state.voice = {
      ...this.state.voice,
      status: 'ready',
      createdTask: null,
      understanding: `已锁定目标「${this.state.target.name}」，等待语音提问或预测`,
    }
    this._emit()
    return this.snapshot()
  }

  clearTarget() {
    this.state.target = null
    this.state.interaction = null
    this.state.voice = {
      ...this.state.voice,
      status: 'idle',
      targetedQuery: null,
      targetedIntent: null,
      createdTask: null,
      understanding: '',
    }
    this._emit()
    return this.snapshot()
  }

  recordTaskCreated(task) {
    if (!task) return this.snapshot()
    this.state.voice = {
      ...this.state.voice,
      createdTask: clone(task),
      understanding: `已创建任务：${task.title}（${task.rootTaskId || task.taskId}）`,
    }
    this._emit()
    return this.snapshot()
  }

  resolveVoice(utterance = '') {
    const text = String(utterance || '').trim()
    
    // 0. 判断是否为取消选择
    if (isUnlockIntent(text)) {
      const prev = this.state.target
      this.clearTarget()
      return {
        intent: INTENT_TYPES.UNLOCK,
        utterance: text,
        previousTarget: prev,
        understanding: '已解除目标锁定',
      }
    }

    // 1. 调用多模态指代消歧与意图解析
    const targetedIntent = resolveTargetedIntent(text, this.snapshot())
    
    let targetedQuery = null
    let understanding = ''

    if (targetedIntent.error === 'NO_TARGET_LOCKED') {
      understanding = '请先指向并锁定目标水库。'
    } else if (targetedIntent.intent === INTENT_TYPES.FORECAST) {
      const target = targetedIntent.target
      const targetName = target ? (target.displayName || target.name) : ''
      const horizonText = targetedIntent.parameters && targetedIntent.parameters.horizon
        ? `未来 ${targetedIntent.parameters.horizon} 小时`
        : '未来时段'
      understanding = `针对「${targetName}」创建${horizonText}流量预测任务`
      
      const knownStatus = target ? structuredStatus(target.data) : {}
      targetedQuery = {
        intent: 'reservoir.forecast',
        target: {
          type: 'reservoir',
          id: target ? target.businessId : '',
          name: targetName,
        },
        utterance: text,
        page: this.state.page.scene,
        businessContext: clone(target ? target.data : {}),
        structuredStatus: knownStatus,
        parameters: targetedIntent.parameters,
        prompt: `当前预测目标：${targetName}（reservoirId=${target ? target.businessId : ''}）。用户指令：${text}。任务类型：水库来水与流量预测。`,
      }
    } else if (targetedIntent.intent === INTENT_TYPES.QUERY && targetedIntent.target) {
      const target = targetedIntent.target
      const targetName = target.displayName || target.name
      const knownStatus = structuredStatus(target.data)
      understanding = `查询「${targetName}」当前状态与指标`
      targetedQuery = {
        intent: 'reservoir.query',
        target: {
          type: 'reservoir',
          id: target.businessId || target.id,
          name: targetName,
        },
        utterance: text,
        page: this.state.page.scene,
        businessContext: clone(target.data || {}),
        structuredStatus: knownStatus,
        prompt: `当前目标：${targetName}（reservoirId=${target.businessId || target.id}）。当前页面：古贤水利枢纽水网 Cesium。当前结构化状态：${Object.keys(knownStatus).length ? JSON.stringify(knownStatus) : '暂无 warning/alarm/status/risk/abnormal 字段'}。仅使用当前系统已知水库数据回答，不得虚构实时异常。用户问题：${text}`,
      }
    }

    this.state.voice = {
      ...this.state.voice,
      status: targetedIntent.error ? 'error' : (targetedQuery ? 'resolved' : 'idle'),
      utterance: text,
      targetedQuery,
      targetedIntent,
      understanding: understanding || (targetedQuery ? `已解析目标：${targetedQuery.target.name}` : ''),
    }
    this._emit()

    return targetedQuery || targetedIntent
  }

  setVoiceStatus(status, patch = {}) {
    this.state.voice = { ...this.state.voice, status, ...clone(patch) }
    this._emit()
    return this.snapshot()
  }

  _emit() {
    const next = this.snapshot()
    this.listeners.forEach(listener => {
      try { listener(next) } catch (_) {}
    })
  }
}

export const multimodalContext = new MultimodalContextStore()

export default multimodalContext
